<script setup lang="ts">
import { computed } from 'vue';
import { UiText, UiIcon, UiTooltip } from '@shopexperts/shared';
import type { AdminExpertClaim } from '@shopexperts/legacy-api';
import ClaimStateChips from './ClaimStateChips.vue';
import { getClaimLifecycle, lifecycleStripClasses } from './claimAppearance';
import { formatAbsoluteTime } from '@/utils/date';

const props = defineProps<{
  claim: AdminExpertClaim;
}>();

const emit = defineEmits<{
  click: [];
}>();

const stripClass = computed(() => lifecycleStripClasses[getClaimLifecycle(props.claim)]);

const submittedDate = computed(() => formatAbsoluteTime(props.claim.created_at, 'date'));
const verifiedDate = computed(() =>
  props.claim.verified_at ? formatAbsoluteTime(props.claim.verified_at, 'date') : '',
);

// A claimed profile blocks approval of any still-undecided claim on it.
const hasClaimConflict = computed(
  () => props.claim.expert.is_claimed && props.claim.decision === null,
);
</script>

<template>
  <article
    class="group cursor-pointer rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-se-dark"
    role="button"
    tabindex="0"
    @click="emit('click')"
    @keydown.enter="emit('click')"
    @keydown.space.prevent="emit('click')"
  >
    <div class="flex bg-white border border-gray-100 rounded-lg overflow-hidden transition-all duration-200 group-hover:shadow-md group-hover:border-gray-200">
      <!-- Lifecycle accent strip -->
      <div class="w-1 shrink-0 self-stretch" :class="stripClass" />

      <div class="flex-1 min-w-0 p-5">
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <!-- Name + chips -->
            <div class="flex items-center gap-2.5 flex-wrap">
              <UiText
                as="h3"
                variant="h5"
                :text="claim.claimant_name || claim.email"
                class="font-semibold text-gray-900"
                :truncate="1"
              />
              <ClaimStateChips :claim="claim" />
            </div>

            <!-- Email -->
            <UiText as="p" variant="caption-normal" class="text-gray-500 mt-0.5 truncate" :text="claim.email" />

            <!-- Claimed expert -->
            <div class="flex items-center gap-1.5 mt-2.5 min-w-0">
              <UiIcon name="listing" class="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <UiText
                as="span"
                variant="caption-normal"
                class="text-gray-700 font-medium truncate"
                :text="claim.expert.name || claim.expert.slug"
              />
              <UiText
                as="span"
                variant="caption-normal"
                class="text-gray-400 truncate"
                :text="`/${claim.expert.slug}`"
              />
              <UiTooltip
                v-if="claim.expert.is_claimed"
                :content="hasClaimConflict
                  ? 'This profile already has an owner — this claim cannot be approved'
                  : 'This profile has an owner'"
              >
                <span
                  class="inline-flex items-center gap-1 shrink-0"
                  :class="hasClaimConflict ? 'text-rose-600' : 'text-gray-400'"
                >
                  <UiIcon name="lock" class="w-3 h-3" />
                  <UiText as="span" variant="caption-normal" text="claimed" />
                </span>
              </UiTooltip>
            </div>
          </div>

          <!-- Dates -->
          <div class="shrink-0 text-right space-y-0.5">
            <UiText
              as="p"
              variant="caption-normal"
              class="text-gray-400 whitespace-nowrap"
              :text="`Submitted ${submittedDate}`"
            />
            <UiText
              v-if="verifiedDate"
              as="p"
              variant="caption-normal"
              class="text-gray-400 whitespace-nowrap"
              :text="`Verified ${verifiedDate}`"
            />
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
